import React, { useEffect, useMemo, useState } from 'react';
import { styled, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import { useAppSelector } from 'app/hooks';
import DrawerContent from './components/DrawerContent';

const openedMixin = (theme: Theme, drawerWidth: number): CSSObject => {
    return {
        width: drawerWidth,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        overflowX: 'hidden',
    };
};

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const getDrawer = (drawerWidth: number) =>
    styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        anchor: 'right',
        ...(open && {
            ...openedMixin(theme, drawerWidth),
            '& .MuiDrawer-paper': openedMixin(theme, drawerWidth),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
    }));

interface RightSidebarType {
    children: React.ReactNode;
}

const RightSidebar: React.FC<RightSidebarType> = ({ children }) => {
    const { isRightSideBarOpened, openRightSidebarAutomatically } = useAppSelector(
        (state) => state.global,
    );
    const [size, setSize] = useState<number>(600);

    useEffect(() => {
        function getSize() {
            if (window.innerWidth > 1600) {
                setSize(600);
            } else {
                setSize(450);
            }
        }
        getSize();
        window.addEventListener('resize', getSize);
        return () => window.removeEventListener('resize', getSize);
    }, []);

    const Drawer = useMemo(
        () => getDrawer(openRightSidebarAutomatically ? 320 : size),
        [openRightSidebarAutomatically, size],
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <Box component="main" sx={{ flexGrow: 1, p: 3, overflow: 'auto', padding: 0 }}>
                {children}
            </Box>
            <Drawer
                variant="permanent"
                anchor="right"
                open={isRightSideBarOpened || openRightSidebarAutomatically}
                sx={{
                    width: openRightSidebarAutomatically ? 320 : size,
                    flexShrink: 0,
                }}
            >
                <DrawerContent size={size} />
            </Drawer>
        </Box>
    );
};

export default RightSidebar;
